import { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'

// Preserves the :snagId param when redirecting old /snags/X URLs (printed
// QR codes, share links sent before the rename) to the new /issues/X.
function SnagAliasRedirect() {
  const { snagId } = useParams<{ snagId: string }>()
  return <Navigate to={`/issues/${snagId}`} replace />
}
import { useAuth } from './lib/auth'
import { useTenant } from './lib/tenant'
import { supabase } from './lib/supabase'
import { hardReset } from './lib/hardReset'

// Eager
import Login from './routes/Login'
import NotFound from './routes/NotFound'
import ToastHost from './components/Toast'
import { withTimeout } from './lib/withTimeout'

// Lazy
const Signup        = lazy(() => import('./routes/Signup'))
const AcceptInvite  = lazy(() => import('./routes/AcceptInvite'))
const Onboarding    = lazy(() => import('./routes/Onboarding'))
const ResetPassword = lazy(() => import('./routes/ResetPassword'))
const SharedIssue   = lazy(() => import('./routes/SharedIssue'))
const SharedIssues  = lazy(() => import('./routes/SharedIssues'))
const SharedProject = lazy(() => import('./routes/SharedProject'))
const TradePortal   = lazy(() => import('./routes/TradePortal'))
const CompleteIssue = lazy(() => import('./routes/CompleteIssue'))
const Home          = lazy(() => import('./routes/Home'))
const Projects      = lazy(() => import('./routes/Projects'))
const ProjectDetail = lazy(() => import('./routes/ProjectDetail'))
const NewSnag       = lazy(() => import('./routes/NewSnag'))
const SnagDetail    = lazy(() => import('./routes/SnagDetail'))
const AllSnags      = lazy(() => import('./routes/AllSnags'))
const Settings      = lazy(() => import('./routes/Settings'))
const QrLabels      = lazy(() => import('./routes/QrLabels'))
const Templates     = lazy(() => import('./routes/Templates'))
const Billing       = lazy(() => import('./routes/Billing'))
const Privacy       = lazy(() => import('./routes/Privacy'))
const Terms         = lazy(() => import('./routes/Terms'))

export default function App() {
  const { phase, session, user, userMissing, errorMessage, takingLong, retry, recoveryMode } = useAuth()
  const { loading: tenantLoading, tenant, tenantMissing } = useTenant()
  const [hasProjects, setHasProjects] = useState<boolean | null>(null)
  // projectsError used to drop the user onto the scary 'Sign out and start
  // over' ErrorShell when the projects-count check timed out. The new
  // optimistic fallback above means we no longer need that error state at
  // all — kept as a const-null so existing references compile, but the
  // path is unreachable in practice.
  const projectsError = null as string | null

  // Important: depend on IDs, not the object references. Otherwise every time
  // auth.tsx re-fetches the user row (background refetch, token refresh, etc.)
  // it produces a new user object, which would re-fire this effect, reset
  // hasProjects to null, and force the whole app back onto a Loading shell
  // for several seconds. That bug made the app feel like it was constantly
  // hanging even when nothing was actually wrong.
  //
  // Also: optimistic cache. Once we know the user has projects, we don't
  // need to re-check on every reload — they're not going to lose all their
  // projects. Bootstrap from localStorage so the first paint after refresh
  // skips the gating spinner entirely.
  useEffect(() => {
    if (!user?.id || !tenant?.id) { setHasProjects(null); return }
    let cancelled = false
    const cacheKey = `ss.hasProjects.${tenant.id}`
    try {
      const cached = localStorage.getItem(cacheKey)
      // Fresh signup short-circuit: if Signup.tsx just set ss.fresh_signup,
      // we know there are no projects yet, so DON'T optimistically render
      // Home — it would crash mid-render before the async count check
      // could correct course to Onboarding. The user saw it as the
      // "Sorry — something tripped up" ErrorBoundary card. Holding on
      // null keeps a clean LoadingShell up for ~200ms until the count
      // check settles, then we go straight to Onboarding → auto-create
      // first project → Home.
      let isFresh = false
      try { isFresh = localStorage.getItem('ss.fresh_signup') === '1' } catch {}
      if (isFresh) setHasProjects(null)
      else if (cached === '1') setHasProjects(true)
      // KEY: if there's no cached flag at all (e.g. user cleared site
      // data or new device) and auth IS valid, optimistically assume
      // they have projects. The router takes them to Home; if it turns
      // out they have none, the projects-count refetch below corrects
      // to Onboarding. Replaces the previous behaviour where a missing
      // flag + slow first fetch dropped the user onto the scary
      // "Sign out and start over" shell.
      else setHasProjects(true)
    } catch {}
    ;(async () => {
      try {
        const { count, error } = await withTimeout(
          supabase.from('projects').select('id', { head: true, count: 'exact' }),
          20000, // bumped from 8s — supabase-js can take longer on first
                 // request after a long-lived session does an auth refresh.
                 // Long timeout doesn't matter because the optimistic
                 // hasProjects = true above already lets the UI render.
          'projects count',
        )
        if (cancelled) return
        if (error) {
          // If we have a positive cached flag, ignore transient errors — user
          // already had projects last time, they don't suddenly need Onboarding.
          if (localStorage.getItem(cacheKey) === '1') return
          // No cache + error: stay optimistic, log only. Don't strand the user.
          console.warn('[projects-count] errored, staying optimistic', error.message)
          return
        }
        const has = (count ?? 0) > 0
        setHasProjects(has)
        try { localStorage.setItem(cacheKey, has ? '1' : '0') } catch {}
      } catch (e: any) {
        if (cancelled) return
        if (localStorage.getItem(cacheKey) === '1') return
        // Same: stay optimistic on timeout, log only.
        console.warn('[projects-count] timed out, staying optimistic', e?.message)
      }
    })()
    return () => { cancelled = true }
  }, [user?.id, tenant?.id])

  // Public share + completion links: short-circuit BEFORE the auth gate.
  // No TabBar on these pages — recipients (customers, contractors) can't
  // navigate to /home / /projects without signing in, so the tab bar
  // was just confusing chrome. Signed-in users who land here can use
  // the browser back button or close the tab.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/share/')) {
    return (
      <Suspense fallback={<LoadingShell />}>
        <Routes>
          {/* Project-scoped report — videos play inline, no login. */}
          <Route path="/share/project/:token" element={<SharedProject />} />
          {/* Multi-issue share — picked from the Issues tab, can span
              multiple projects. */}
          <Route path="/share/issues/:token" element={<SharedIssues />} />
          {/* Trade portal — contractor sees every issue assigned to them
              on a project from a single magic link. */}
          <Route path="/share/trade/:token" element={<TradePortal />} />
          {/* Single-issue share — predates project shares. */}
          <Route path="/share/:token" element={<SharedIssue />} />
          <Route path="*" element={<SharedIssue />} />
        </Routes>
      </Suspense>
    )
  }
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/complete/')) {
    return (
      <Suspense fallback={<LoadingShell />}>
        <Routes>
          <Route path="/complete/:token" element={<CompleteIssue />} />
          <Route path="*" element={<CompleteIssue />} />
        </Routes>
      </Suspense>
    )
  }
  // Public legal pages — required by App Store + Play Store listings. Must
  // be reachable without authentication so the stores can review them.
  if (typeof window !== 'undefined' && (window.location.pathname === '/privacy' || window.location.pathname === '/terms')) {
    return (
      <Suspense fallback={<LoadingShell />}>
        <Routes>
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </Suspense>
    )
  }

  // Phase-driven shells — no more ambiguous "we don't know what's happening"
  // states. Every branch corresponds to a single, knowable reality.
  if (phase === 'init')   return <LoadingShell />
  if (phase === 'loading') return <LoadingShell longExplanation={takingLong} />
  if (phase === 'error') {
    return <RetryShell
      message={errorMessage || 'Could not load your account.'}
      onRetry={retry}
    />
  }

  // Password-recovery short-circuit. Supabase has issued a temporary session
  // off the recovery email; the user MUST set a new password before they can
  // do anything else. Hop straight to ResetPassword regardless of route.
  if (recoveryMode) {
    return (
      <Suspense fallback={<LoadingShell />}>
        <ResetPassword />
      </Suspense>
    )
  }

  // Public
  if (!session) {
    // Preserve the deep link (e.g. /projects/abc/new?location=Room+304 from
    // a scanned QR) so the user lands back on it after sign-in. Stored as
    // a single-shot localStorage key; consumed below once we're signed in.
    if (typeof window !== 'undefined') {
      const p = window.location.pathname + window.location.search
      if (p !== '/' && !p.startsWith('/signup') && !p.startsWith('/invite/')) {
        try { localStorage.setItem('ss.return_to', p) } catch {}
      }
    }
    return (
      <Suspense fallback={<LoadingShell />}>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
    )
  }

  // Just signed in? If there's a saved deep link, jump there once.
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('ss.return_to')
      if (saved && saved !== window.location.pathname + window.location.search) {
        localStorage.removeItem('ss.return_to')
        window.history.replaceState(null, '', saved)
      } else if (saved) {
        localStorage.removeItem('ss.return_to')
      }
    } catch {}
  }

  // The auth/tenant queries distinguish "server confirmed missing" from
  // "couldn't reach server" — only the former should drop the user into
  // Onboarding (the account-setup flow). For tenant load failures we show
  // a real error with retry instead of the misleading "reconnecting" card.
  if (userMissing) return <Onboarding />
  if (!user) return <LoadingShell />  // belt-and-braces — phase machine should have handled this
  if (tenantLoading) return <LoadingShell />
  if (tenantMissing) return <Onboarding />
  if (!tenant) {
    return <RetryShell
      message="Couldn't load your organisation data."
      onRetry={retry}
    />
  }

  if (hasProjects === false && projectsError) return <ErrorShell message={projectsError} />
  if (hasProjects === false) return <Onboarding />
  if (hasProjects === null) return <LoadingShell />

  // Single role — no per-role branching
  return (
    <Suspense fallback={<LoadingShell />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:projectId" element={<ProjectDetail />} />
        <Route path="/projects/:projectId/new" element={<NewSnag />} />
        <Route path="/projects/:projectId/qr" element={<QrLabels />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/issues" element={<AllSnags />} />
        <Route path="/issues/:snagId" element={<SnagDetail />} />
        {/* Diary route removed — redirect any old links to Issues so
            anyone who bookmarked /diary still lands somewhere useful. */}
        <Route path="/diary" element={<Navigate to="/issues" replace />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
        {/* Back-compat redirects — /snags was the prior URL; printed QR
            codes / share links from before the rename still land. */}
        <Route path="/snags" element={<Navigate to="/issues" replace />} />
        <Route path="/snags/:snagId" element={<SnagAliasRedirect />} />
        <Route path="/sites" element={<Navigate to="/projects" replace />} />
        <Route path="/sites/:siteId" element={<Navigate to="/projects" replace />} />
        <Route path="/team" element={<Navigate to="/settings" replace />} />
        <Route path="/customers" element={<Navigate to="/settings" replace />} />
        <Route path="/trades" element={<Navigate to="/projects" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ToastHost />
    </Suspense>
  )
}

function LoadingShell({ longExplanation }: { longExplanation?: boolean } = {}) {
  const [showEscape, setShowEscape] = useState(false)
  useEffect(() => { const t = setTimeout(() => setShowEscape(true), 12000); return () => clearTimeout(t) }, [])
  return (
    <>
      <header className="app-bar"><h1>Signed and Sorted</h1></header>
      <main>
        <p className="muted" style={{ marginTop: 'var(--space-5)', textAlign: 'center', fontSize: 15 }}>
          {longExplanation ? '⏳ Connecting to your account…' : 'Loading…'}
        </p>
        {longExplanation && (
          <p className="muted" style={{ marginTop: 'var(--space-2)', textAlign: 'center', fontSize: 12, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
            This can take 20–30 seconds if you've been idle for a while — our server takes a moment to wake up.
          </p>
        )}
        {showEscape && (
          <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
            <p className="muted" style={{ fontSize: 12, marginBottom: 'var(--space-2)' }}>Still stuck?</p>
            <button className="link-btn" onClick={hardReset}>Sign out and start over</button>
          </div>
        )}
      </main>
    </>
  )
}

function RetryShell({ message, onRetry }: { message: string; onRetry: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false)
  async function retry() {
    setBusy(true)
    try { await onRetry() } finally { setBusy(false) }
  }
  return (
    <>
      <header className="app-bar"><h1>Signed and Sorted</h1></header>
      <main style={{ marginTop: 'var(--space-5)' }}>
        <div className="card" style={{ borderColor: 'var(--danger)' }}>
          <h3 style={{ color: 'var(--danger)' }}>Couldn't load your data</h3>
          <p style={{ fontSize: 14, marginTop: 'var(--space-2)' }}>{message}</p>
          <p style={{ fontSize: 13, color: 'var(--ink-mute)', marginTop: 'var(--space-2)' }}>
            This usually clears up after a moment. Tap retry first; if that doesn't help, sign out and back in for a fresh session.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" style={{ width: 'auto' }} onClick={retry} disabled={busy}>
              {busy ? 'Retrying…' : 'Retry'}
            </button>
            <button className="btn btn-secondary" style={{ width: 'auto' }} onClick={hardReset}>
              Sign out and start over
            </button>
          </div>
        </div>
      </main>
    </>
  )
}

function ErrorShell({ message }: { message: string }) {
  return (
    <>
      <header className="app-bar"><h1>Signed and Sorted</h1></header>
      <main style={{ marginTop: 'var(--space-5)' }}>
        <div className="card" style={{ borderColor: 'var(--danger)' }}>
          <h3 style={{ color: 'var(--danger)' }}>Couldn't reach your data</h3>
          <p style={{ fontSize: 14, marginTop: 'var(--space-2)' }}>{message}</p>
          <p style={{ fontSize: 13, color: 'var(--ink-mute)', marginTop: 'var(--space-2)' }}>
            This usually means your session is stale (often because the app updated since you signed in). The button below clears your local data and signs you out fully — your data on the server is safe.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 'var(--space-3)', width: 'auto' }} onClick={hardReset}>Sign out and start over</button>
        </div>
      </main>
    </>
  )
}
